import type { BillingPlan, OrgBilling } from "@kontextwerk/control-web-client"

export type KehrblattPlanFamily = "kehrbase" | "kehrchat" | "kehrtalk"
export type KehrblattContactChannel = "EMAIL" | "MOBILE_MESSAGE" | "PHONE" | "LETTER"

export type KehrblattPlanCapabilities = {
  family: KehrblattPlanFamily | null
  isKehrBase: boolean
  isKehrChat: boolean
  isKehrTalk: boolean
  hasAiAgents: boolean
  hasUsage: boolean
  hasAnalytics: boolean
  hasSessionConfig: boolean
  hasContactProfiles: boolean
  hasMobileMessage: boolean
  hasPhone: boolean
  hasVoice: boolean
  allowedContactChannels: KehrblattContactChannel[]
}

type BillingPlanCapabilityEntry = {
  billing?: OrgBilling | null
  plan?: BillingPlan | null
}

const PERMISSIVE_CAPABILITIES: KehrblattPlanCapabilities = {
  family: null,
  isKehrBase: false,
  isKehrChat: false,
  isKehrTalk: false,
  hasAiAgents: true,
  hasUsage: true,
  hasAnalytics: true,
  hasSessionConfig: true,
  hasContactProfiles: true,
  hasMobileMessage: true,
  hasPhone: true,
  hasVoice: true,
  allowedContactChannels: ["EMAIL", "MOBILE_MESSAGE", "PHONE", "LETTER"],
}

const capabilitiesForFamily = (family: KehrblattPlanFamily | null): KehrblattPlanCapabilities => {
  if (family === "kehrbase") {
    return {
      family,
      isKehrBase: true,
      isKehrChat: false,
      isKehrTalk: false,
      hasAiAgents: false,
      hasUsage: true,
      hasAnalytics: false,
      hasSessionConfig: false,
      hasContactProfiles: false,
      hasMobileMessage: true,
      hasPhone: false,
      hasVoice: false,
      allowedContactChannels: ["EMAIL", "MOBILE_MESSAGE", "LETTER"],
    }
  }

  if (family === "kehrchat") {
    return {
      family,
      isKehrBase: false,
      isKehrChat: true,
      isKehrTalk: false,
      hasAiAgents: true,
      hasUsage: true,
      hasAnalytics: true,
      hasSessionConfig: true,
      hasContactProfiles: true,
      hasMobileMessage: true,
      hasPhone: false,
      hasVoice: false,
      allowedContactChannels: ["EMAIL", "MOBILE_MESSAGE", "LETTER"],
    }
  }

  if (family === "kehrtalk") {
    return {
      family,
      isKehrBase: false,
      isKehrChat: false,
      isKehrTalk: true,
      hasAiAgents: true,
      hasUsage: true,
      hasAnalytics: true,
      hasSessionConfig: true,
      hasContactProfiles: true,
      hasMobileMessage: true,
      hasPhone: true,
      hasVoice: true,
      allowedContactChannels: ["EMAIL", "MOBILE_MESSAGE", "PHONE", "LETTER"],
    }
  }

  return { ...PERMISSIVE_CAPABILITIES }
}

const normalize = (value?: string | null) => (value || "").trim().toLowerCase()

const familyFromCanonicalValue = (value?: string | null): KehrblattPlanFamily | null => {
  const normalized = normalize(value)

  if (!normalized) return null

  if (normalized.startsWith("kehrbase") || normalized.startsWith("carebase")) return "kehrbase"

  if (normalized.startsWith("kehrchat") || normalized.startsWith("carechat")) return "kehrchat"

  if (normalized.startsWith("kehrtalk") || normalized.startsWith("caretalk")) return "kehrtalk"

  return null
}

const familyFromLegacyType = (value?: string | null): KehrblattPlanFamily | null => {
  const normalized = normalize(value)

  if (!normalized) return null

  if (normalized.includes("base")) return "kehrbase"

  if (normalized.includes("talk") || normalized.includes("voice")) return "kehrtalk"

  if (normalized.includes("chat")) return "kehrchat"

  return null
}

export const resolveKehrblattPlanFamily = (
  entry?: BillingPlanCapabilityEntry | null,
): KehrblattPlanFamily | null => {
  const plan = entry?.plan ?? null
  const billing = entry?.billing ?? null

  const canonicalCandidates = [
    plan?.family_id,
    billing?.plan_family_id,
    plan?.slug,
    plan?.id,
    billing?.plan_version_id,
  ]
  for (const candidate of canonicalCandidates) {
    const family = familyFromCanonicalValue(candidate)

    if (family) return family
  }

  const billingKind = (plan as { billing_kind?: string | null } | null)?.billing_kind

  if (billingKind === "free") return "kehrbase"

  const legacyCandidates = [plan?.type, billing?.plan_type]
  for (const candidate of legacyCandidates) {
    const family = familyFromLegacyType(candidate)

    if (family) return family
  }

  return null
}

export const resolveKehrblattPlanCapabilities = (
  entry?: BillingPlanCapabilityEntry | null,
): KehrblattPlanCapabilities => {
  const family = resolveKehrblattPlanFamily(entry)

  if (!family) return { ...PERMISSIVE_CAPABILITIES }

  return capabilitiesForFamily(family)
}

export const planSupportsVoice = (planType?: string | null): boolean => {
  const family = familyFromLegacyType(planType)

  if (!family) return true

  return capabilitiesForFamily(family).hasVoice
}

export const planSupportsMobileMessage = (planType?: string | null): boolean => {
  const family = familyFromLegacyType(planType)

  if (!family) return true

  return capabilitiesForFamily(family).hasMobileMessage
}
